import React from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineChevronDoubleLeft, HiOutlineXMark } from "react-icons/hi2";
import { IoAirplaneOutline } from "react-icons/io5";
import { NavLink, useLocation } from "react-router-dom";
import { getActiveNavPath, getNavigationGroups } from "../../app/navigation";
import useAuth from "../../hooks/useAuth";
import useLocale from "../../hooks/useLocale";
import { getRoleLabel, getScopeLabel } from "../../utils/access";
import cn from "../../ui/cn";

const NavigationItem = ({ item, active, collapsed, onNavigate, label }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.end}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      title={collapsed ? label : undefined}
      className={cn(
        "group flex min-h-9 items-center gap-2.5 rounded-lg px-2 py-1 text-sm font-medium transition focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-300/60",
        collapsed && "justify-center px-0",
        active
          ? "bg-white/[0.12] text-white"
          : "text-slate-300 hover:bg-white/[0.06] hover:text-white",
      )}
    >
      <span
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
          active ? "bg-accent-400/20 text-accent-300" : "text-slate-400 group-hover:text-accent-200",
        )}
      >
        <Icon size={18} aria-hidden="true" />
      </span>
      {!collapsed ? <span className="min-w-0 flex-1 truncate">{label}</span> : <span className="sr-only">{label}</span>}
    </NavLink>
  );
};

function Sidebar({ open, isDesktop, collapsed, onToggleCollapsed, onClose }) {
  const { t } = useTranslation();
  const { isRTL } = useLocale();
  const { auth, user } = useAuth();
  const location = useLocation();
  const role = auth?.role;
  const groups = getNavigationGroups(role);
  const activePath = getActiveNavPath(location.pathname);

  const organizationName =
    user?.platform?.name || user?.agency?.name || auth?.organizationName || t("app.name");
  const organizationType =
    (user?.platform ? "platform" : "") || (user?.agency ? "agency" : "") || auth?.organizationType;

  const closedTransform = isRTL ? "translate-x-full" : "-translate-x-full";

  return (
    <aside
      id="admin-sidebar"
      aria-label={t("header.navigation")}
      aria-hidden={!open && !isDesktop ? true : undefined}
      className={cn(
        "fixed inset-y-0 start-0 z-50 flex shrink-0 flex-col bg-slate-950 text-white shadow-2xl transition-[transform,width] duration-200 xl:sticky xl:top-0 xl:h-screen xl:translate-x-0 xl:shadow-none",
        collapsed ? "w-sidebar-collapsed" : "w-sidebar",
        open ? "translate-x-0" : closedTransform,
      )}
    >
      <div className={cn("flex h-16 items-center gap-3 border-b border-white/10 px-4", collapsed && "justify-center px-0")}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-accent-300 to-brand-600 text-white">
          <IoAirplaneOutline size={20} aria-hidden="true" />
        </span>
        {!collapsed ? (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold tracking-tight">{t("app.name")}</p>
            <p className="truncate text-[11px] text-slate-400">{t("app.controlCenter")}</p>
          </div>
        ) : null}
        {!isDesktop ? (
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white/10 hover:text-white"
            aria-label={t("header.closeNavigation")}
          >
            <HiOutlineXMark size={22} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {groups.map((group) => (
          <section key={group.id} className="mb-3 last:mb-0">
            {!collapsed ? (
              <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {t(group.labelKey)}
              </p>
            ) : (
              <div className="mx-2 mb-2 border-t border-white/10 first:hidden" aria-hidden="true" />
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.path}>
                  <NavigationItem
                    item={item}
                    label={t(item.titleKey)}
                    active={activePath === item.path}
                    collapsed={collapsed}
                    onNavigate={() => {
                      if (!isDesktop) onClose();
                    }}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>

      <div className={cn("flex items-center gap-2 border-t border-white/10 px-3 py-2.5", collapsed && "justify-center px-2")}>
        {!collapsed ? (
          <div className="min-w-0 flex-1 px-1">
            <p className="truncate text-sm font-semibold" title={organizationName}>
              {organizationName}
            </p>
            <p className="truncate text-[11px] text-slate-400">
              {getScopeLabel(organizationType, t)} · {getRoleLabel(role, t)}
            </p>
          </div>
        ) : null}
        {isDesktop ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-pressed={collapsed}
            aria-label={collapsed ? t("shell.expandNavigation") : t("shell.collapseNavigation")}
            title={collapsed ? t("shell.expandNavigation") : t("shell.collapseNavigation")}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white/[0.08] hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-300/60"
          >
            <HiOutlineChevronDoubleLeft
              size={16}
              aria-hidden="true"
              className={cn("rtl-flip transition", collapsed && "rotate-180")}
            />
          </button>
        ) : null}
      </div>
    </aside>
  );
}

export default Sidebar;
